import React, { useState, useContext, useCallback, useEffect } from "react";
import { NotificationManager } from "react-notifications";
import Select from "react-select";
import useApi from "../Api/Api";
import { useParams } from "react-router-dom";
import WideSpreadInput from "../Components/WideSpreadInput";
import SalesLayout from "./SalesLayout";
import EventLayout from "./EventLayout";
import StatesList from "../Components/StatesList";
import { FilterContext } from "../FilterContextProvider";
import Button from "../Components/Button";
import useLocalState from "../hooks/useLocalState";
import { UserContext } from "../Auth/UserContextProvider";
import CleaveInput from "../Components/CleaveInput";
import storageManager from "../Utility/StorageManager";
import { kNumberOfUpdatesAllowed } from "./constants";
import OnlineWrapper from "./OnlineWrapper";
import OfflineWrapper from "./OfflineWrapper";

const EventDetails = () => {
  const [isFetching, setIsFetching] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const { filterData } = useContext(FilterContext);
  const api = useApi();
  const params = useParams();
  const [showButtons, setShowButtons] = useState(false);
  const [eventTypes, setEventTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dataCached, setDataCached] = useState(false);
  const { isAdmin } = useContext(UserContext);

  const [tempTaxPercent, setTempTaxPercent] = useLocalState(`event/${params.eventId}/tempTaxPercent`, "");

  const [tempCCPercent, setTempCCPercent] = useLocalState(`event/${params.eventId}/tempCCPercent`, "");

  const [event, setEvent] = useLocalState(`event/${params.eventId}/info`, {
    artist_id: filterData.artist_id,
    id: params.eventId,
    tax_rate: tempTaxPercent / 100,
  });

  const editedEvent = {
    ...event,
    tax_rate: tempTaxPercent / 100,
    cc_rate: tempCCPercent / 100,
  };

  const [clothingSetup, setClothingSetup] = useLocalState(`event/${params.eventId}/clothingSetup`, {
    hall_rate: "",
    event_id: params.eventId,
  });

  const [otherSetup, setOtherSetup] = useLocalState(`event/${params.eventId}/otherSetup`, {
    hall_rate: "",
    event_id: params.eventId,
  });

  const [musicSetup, setMusicSetup] = useLocalState(`event/${params.eventId}/musicSetup`, {
    hall_rate: "",
    event_id: params.eventId,
  });

  const stateOptions = StatesList.map((state) => ({
    label: state.name,
    value: state.abbreviation,
  }));

  const eventTypeOptions = eventTypes.map(({ id, name }) => ({
    label: name,
    value: id,
  }));

  const handleChange = ({ target: { id, value } }, offlineState) => {
    setDataCached(offlineState);
    setShowButtons(true);
    setEvent({
      ...event,
      [id]: value,
    });
  };

  const handleTempTaxPercent = (e, offlineState) => {
    setDataCached(offlineState);
    setShowButtons(true);
    setTempTaxPercent(e.target.value);
  };

  const handleHallRateChange = ({ target: { id, value } }, offlineState) => {
    setDataCached(offlineState);
    setShowButtons(true);
    switch (id) {
      case "clothing_hall_rate":
        return setClothingSetup({
          ...clothingSetup,
          temp_decimal: value,
          hall_rate: value / 100,
        });
      case "other_hall_rate":
        return setOtherSetup({
          ...otherSetup,
          temp_decimal: value,
          hall_rate: value / 100,
        });
      case "music_hall_rate":
        return setMusicSetup({
          ...musicSetup,
          temp_decimal: value,
          hall_rate: value / 100,
        });
      default:
    }
  };

  const handleSelect = (option, name, offlineState) => {
    setDataCached(offlineState);
    setShowButtons(true);
    setEvent({
      ...event,
      [name.name]: option.value,
    });
  };

  const revert = () => {
    let check = "";
    if (!isAdmin && event.number_of_updates === 0) {
      check = window.confirm("Are you sure you want to revert? All unsaved changes will be lost.");
    } else {
      check = true;
    }
    if (check) {
      setShowButtons(false);
      setEvent(JSON.parse(storageManager.getItem(`event/${params.eventId}/originalEvent`)));
      setTempTaxPercent(JSON.parse(storageManager.getItem(`event/${params.eventId}/originalEvent`)).tax_rate * 100);
      setClothingSetup({
        ...JSON.parse(storageManager.getItem(`event/${params.eventId}/originalClothingSetup`)),
        temp_decimal:
          JSON.parse(storageManager.getItem(`event/${params.eventId}/originalClothingSetup`)).hall_rate * 100,
      });
      setOtherSetup({
        ...JSON.parse(storageManager.getItem(`event/${params.eventId}/originalOtherSetup`)),
        temp_decimal: JSON.parse(storageManager.getItem(`event/${params.eventId}/originalOtherSetup`)).hall_rate * 100,
      });
      setMusicSetup({
        ...JSON.parse(storageManager.getItem(`event/${params.eventId}/originalMusicSetup`)),
        temp_decimal: JSON.parse(storageManager.getItem(`event/${params.eventId}/originalMusicSetup`)).hall_rate * 100,
      });
    }
  };

  const afterSubmit = () => {
    getEvent();
    getEventCategorySetups();
  };

  let hallRatesToSave = [];

  const handleUpdateEvent = (e) => {
    e.preventDefault();
    if (!clothingSetup.category_id) {
      clothingSetup.category_id = categories.find((one) => one.name === "Clothing").id;
      clothingSetup.name = "Clothing";
    }
    if (!otherSetup.category_id) {
      otherSetup.category_id = categories.find((one) => one.name === "Other").id;
      otherSetup.name = "Other";
    }
    if (!musicSetup.category_id) {
      musicSetup.category_id = categories.find((one) => one.name === "Music").id;
      musicSetup.name = "Music";
    }
    setIsFetching(true);
    hallRatesToSave.push(clothingSetup, otherSetup, musicSetup);
    api
      .put(`/events/${event.id}`, {
        event: {
          ...editedEvent,
          event_category_setups_attributes: hallRatesToSave.map((each) => ({
            id: each.id,
            name: each.name,
            hall_rate: each.hall_rate,
            taxed: each.taxed,
            gross_sales: each.gross_sales,
            gross_tax: each.gross_tax,
            gross_hall: each.gross_hall,
            category_id: each.category_id,
            event_id: event.id,
          })),
        },
      })
      .then((response) => {
        NotificationManager.success("Event successfully updated", "Success", 2000);
        afterSubmit();
        if (hallRatesToSave === []) {
          getEventCategorySetups();
        }
        return response.data;
      })
      .catch((error) => {
        setErrorMessage(error.response.data);
      })
      .finally(() => {
        setIsFetching(false);
      });
    setShowButtons(false);
  };

  const handleOfflineSubmit = () => {
    setShowButtons(false);
    NotificationManager.warning(
      "You are currently operating offline. Your changes have been cached for now, and you will be able to sync once you are online again.",
      "Warning",
      10000
    );
  };

  const getEvent = useCallback(() => {
    api.get(`/events/${params.eventId}`).then((r) => {
      setEvent(r.data);
      setTempTaxPercent(r.data.tax_rate * 100);
      setTempCCPercent(r.data.cc_rate * 100);
      storageManager.setItem(`event/${params.eventId}/originalEvent`, JSON.stringify(r.data));
    });
  }, [api, setEvent, params.eventId, setTempCCPercent, setTempTaxPercent]);

  const getEventCategorySetups = useCallback(() => {
    api.get(`/event_category_setups?event_id=${params.eventId}`).then((r) => {
      storageManager.setItem(
        `event/${params.eventId}/originalClothingSetup`,
        JSON.stringify(r.data.find((one) => one.name === "Clothing"))
      );
      storageManager.setItem(
        `event/${params.eventId}/originalOtherSetup`,
        JSON.stringify(r.data.find((one) => one.name === "Other"))
      );
      storageManager.setItem(
        `event/${params.eventId}/originalMusicSetup`,
        JSON.stringify(r.data.find((one) => one.name === "Music"))
      );
      setClothingSetup({
        ...r.data.find((one) => one.name === "Clothing"),
        temp_decimal: r.data.find((one) => one.name === "Clothing")?.hall_rate * 100,
      });
      setOtherSetup({
        ...r.data.find((one) => one.name === "Other"),
        temp_decimal: r.data.find((one) => one.name === "Other")?.hall_rate * 100,
      });
      setMusicSetup({
        ...r.data.find((one) => one.name === "Music"),
        temp_decimal: r.data.find((one) => one.name === "Music")?.hall_rate * 100,
      });
    });
  }, [api, params.eventId, setClothingSetup, setOtherSetup, setMusicSetup]);

  useEffect(() => {
    !storageManager.getItem(`event/${params.eventId}/info`) && getEvent();
  }, [getEvent, params.eventId]);

  useEffect(() => {
    !storageManager.getItem(`event/${params.eventId}/clothingSetup`) && getEventCategorySetups();
  }, [getEventCategorySetups, params.eventId]);

  useEffect(() => {
    !storageManager.getItem(`event/${params.eventId}/tempTaxPercent`) && setTempTaxPercent(event.tax_rate * 100);
  }, [event.tax_rate, params.eventId, setTempTaxPercent]);

  useEffect(() => {
    !storageManager.getItem(`event/${params.eventId}/tempCCPercent`) && setTempCCPercent(event.cc_rate * 100);
  }, [event.cc_rate, params.eventId, setTempCCPercent]);

  useEffect(() => {
    api.get(`/categories`).then((r) => {
      setCategories(r.data);
    });
    api.get(`/event_types`).then((r) => {
      setEventTypes(r.data);
    });
  }, [api]);

  const renderForm = (offlineState) => {
    return (
      <form>
        <div className="mb-5 text-xl font-medium text-black">Event Information</div>
        <hr className="my-3"></hr>
        <div className="flex items-center text-xs">
          <label htmlFor="date" className="w-1/3">
            Date *
          </label>
          <div className="w-1/2">
            <input
              value={event.date ? event.date.split("T")[0] : ""}
              type="date"
              id="date"
              name="date"
              className="h-8 pl-2 ml-5 font-light rounded-md border w-full  text-xs"
              onChange={(e) => handleChange(e, offlineState)}
              required
            ></input>
          </div>
        </div>
        {errorMessage?.errors?.date && <div className="text-red-500 pt-2">Name {errorMessage.errors.date}</div>}
        <hr className="my-3"></hr>
        <div>
          <WideSpreadInput
            id="name"
            name="name"
            label="Venue *"
            value={event.name ?? ""}
            onChange={(e) => handleChange(e, offlineState)}
            required
          />
        </div>
        {errorMessage?.errors?.name && <div className="text-red-500 pt-2">Name {errorMessage.errors.name}</div>}
        <hr className="my-3"></hr>
        <div>
          <WideSpreadInput
            id="capacity"
            name="capacity"
            label="Capacity"
            value={event.capacity ?? ""}
            onChange={(e) => handleChange(e, offlineState)}
          />
        </div>
        <hr className="my-3"></hr>
        <div className="h-8 flex items-center text-xs">
          <label html="event_type" className="w-1/3">
            Event Type
          </label>
          <div className="w-1/2 ml-5">
            <Select
              id="event_type"
              name="event_type_id"
              placeholder="Select Event Type"
              options={eventTypeOptions}
              value={eventTypeOptions?.filter((each) => each.value === event.event_type_id)}
              onChange={(option, name) => handleSelect(option, name, offlineState)}
            />
          </div>
        </div>
        <hr className="my-3"></hr>
        <fieldset className="flex text-xs">
          <div className="w-1/3 self-center">
            <legend>Location</legend>
          </div>
          <div className="w-1/2">
            <div className="ml-5 flex w-full">
              <input
                id="city"
                placeholder="City"
                value={event.city ?? ""}
                className="h-8 pl-2 rounded-md border w-1/2"
                onChange={(e) => handleChange(e, offlineState)}
              />
              <div className="h-8 mx-2 w-1/4">
                <Select
                  id="state"
                  name="state"
                  placeholder="State"
                  className="font-light"
                  options={stateOptions}
                  value={stateOptions?.filter((each) => each.value === event.state)}
                  onChange={(option, name) => handleSelect(option, name, offlineState)}
                />
              </div>
              <input
                id="zip_code"
                placeholder="Zip Code"
                value={event.zip_code ?? ""}
                className="h-8 pl-2 rounded-md border w-1/4"
                onChange={(e) => handleChange(e, offlineState)}
              />
            </div>
          </div>
        </fieldset>
        <hr className="my-3"></hr>
        <fieldset className="flex text-xs">
          <div className="w-1/3 self-center">
            <legend>Fees</legend>
          </div>
          <div className="w-1/2">
            <div className="ml-5 flex w-full">
              <CleaveInput
                name="clothing_hall_rate"
                value={clothingSetup?.temp_decimal ?? ""}
                onChange={(e) => handleHallRateChange(e, offlineState)}
                cleaveOptions={{
                  numeral: true,
                  numeralPositiveOnly: true,
                  numeralDecimalScale: 3,
                }}
                inputClassName="w-5/6"
                postfix={"%"}
                required
              />
              <CleaveInput
                name="other_hall_rate"
                value={otherSetup?.temp_decimal ?? ""}
                onChange={(e) => handleHallRateChange(e, offlineState)}
                cleaveOptions={{
                  numeral: true,
                  numeralPositiveOnly: true,
                  numeralDecimalScale: 3,
                }}
                inputClassName="w-5/6"
                postfix={"%"}
                required
              />
              <CleaveInput
                name="music_hall_rate"
                value={musicSetup?.temp_decimal ?? ""}
                onChange={(e) => handleHallRateChange(e, offlineState)}
                cleaveOptions={{
                  numeral: true,
                  numeralPositiveOnly: true,
                  numeralDecimalScale: 3,
                }}
                inputClassName="w-5/6"
                postfix={"%"}
                required
              />
            </div>
          </div>
        </fieldset>
        <hr className="my-3"></hr>
        <fieldset className="flex text-xs">
          <div className="w-1/3 self-center">
            <legend>Tax rate</legend>
          </div>
          <div className="ml-5 flex w-1/2">
            <CleaveInput
              name="tempTaxPercent"
              value={tempTaxPercent ?? ""}
              onChange={(e) => handleTempTaxPercent(e, offlineState)}
              cleaveOptions={{
                numeral: true,
                numeralPositiveOnly: true,
                numeralDecimalScale: 3,
              }}
              inputClassName="w-5/6"
              postfix={"%"}
              required
            />
          </div>
        </fieldset>
        <hr className="my-3"></hr>
        <fieldset className="flex text-xs">
          <div className="w-1/3 self-center">
            <legend>Currency Exchange Rate</legend>
          </div>
          <div className="ml-5 flex w-1/2">
            <CleaveInput
              name="currency_exchange_rate"
              value={event.currency_exchange_rate ? event.currency_exchange_rate.toString() : ""}
              onChange={(e) => handleChange(e, offlineState)}
              cleaveOptions={{
                numeral: true,
                numeralPositiveOnly: true,
                numeralDecimalScale: 5,
              }}
              inputClassName="w-5/6"
              placeholder="e.g. 0.72"
            />
          </div>
        </fieldset>
        <hr className="my-3"></hr>
        <div className="mt-8 tracking-normal font-medium text-lg text-black">Venue Rep Contact</div>
        <hr className="my-3"></hr>
        <div>
          <WideSpreadInput
            id="venue_rep_name"
            name="venue_rep_name"
            label="Name"
            value={event.venue_rep_name ?? ""}
            onChange={(e) => handleChange(e, offlineState)}
          />
        </div>
        <hr className="my-3"></hr>
        <div>
          <WideSpreadInput
            id="venue_rep_email"
            name="venue_rep_email"
            label="Email"
            value={event.venue_rep_email ?? ""}
            onChange={(e) => handleChange(e, offlineState)}
          />
        </div>
        <hr className="my-3"></hr>
        <div>
          <WideSpreadInput
            id="venue_rep_phone"
            name="venue_rep_phone"
            label="Phone"
            value={event.venue_rep_phone ?? ""}
            onChange={(e) => handleChange(e, offlineState)}
          />
        </div>
        <hr className="my-3"></hr>
        {showButtons && (event.number_of_updates < kNumberOfUpdatesAllowed || isAdmin) ? (
          <div className="flex justify-center">
            <Button className="button button-white mx-2" onClick={revert} text="Revert" disabled={isFetching}></Button>
            <OnlineWrapper>
              {!dataCached && (
                <Button
                  text={isFetching ? `Loading ${"\u00A0"}` : "Update"}
                  onClick={handleUpdateEvent}
                  disabled={isFetching}
                  inProgress={isFetching}
                  className="button"
                ></Button>
              )}
            </OnlineWrapper>
            <OfflineWrapper>
              <Button
                className="button"
                onClick={handleOfflineSubmit}
                text={isFetching ? `Loading ${"\u00A0"}` : "Update"}
                disabled={isFetching}
                inProgress={isFetching}
              ></Button>
            </OfflineWrapper>
          </div>
        ) : (
          <>
            {showButtons && (
              <div className="flex justify-center">
                <Button
                  className="button button-white mx-2"
                  onClick={revert}
                  text="Revert"
                  disabled={isFetching}
                ></Button>
                <span className="text-red-500 text-sm">Contact an admin to submit further changes</span>
              </div>
            )}
          </>
        )}
        <div className="flex justify-center mt-8">
          {errorMessage?.error && <div className="text-red-500 pb-2">{errorMessage.error}</div>}
        </div>
      </form>
    );
  };

  return (
    <SalesLayout>
      <EventLayout dataCached={dataCached} setDataCached={setDataCached} afterSubmit={afterSubmit}>
        <OnlineWrapper>{renderForm(false)}</OnlineWrapper>
        <OfflineWrapper>{renderForm(true)}</OfflineWrapper>
      </EventLayout>
    </SalesLayout>
  );
};

export default EventDetails;
